<template>
  <div class="comment-card py-4 px-5">
    <div class="comment-head flex items-center gap-2.5 select-none">
      <nd-img
        v-if="shouldShowDefaultAvatar"
        :src="commenterAvatarImagePath"
        alt="fail to fetch image"
        width="28"
        height="28"
        class="aspect-square rounded-full object-cover"
      />
      <div v-else class="avatar flex items-center justify-center text-white rounded-full w-7 h-7" :class="avatarBackgroundColorClass">
        <div class="text-base">{{ commenterFirstLetter }}</div>
      </div>
      <div class="right-group flex flex-col gap-1">
        <span class="name text-sm">{{ displayCommenterName }}</span>
        <div class="flex items-center gap-1.5">
          <n-rate :default-value="props.comment.rating" :count="5" :show-text="false" readonly size="small">
            <svg-icon name="star-filled" class="w-4 h-4" />
          </n-rate>
          <span class="date text-sm text-gray-80">{{ commentDate }}</span>
        </div>
      </div>
    </div>
    <div class="content mt-4">{{ props.comment.rating_description }}</div>
    <div class="tags mt-4 flex flex-wrap gap-2">
      <div v-for="tag in props.comment.tags" :key="tag.tag_id" class="tag bg-gray-20 py-0.5 px-2 rounded">
        <span class="text-gray-80 text-xs">{{ tag.tag_name }}</span>
      </div>
    </div>
    <div v-if="externalSource" class="mt-4 flex items-center gap-1 select-none">
      <n-tooltip
        v-model:show="isSourceTooltipVisible"
        placement="top-start"
        :show-arrow="false"
        :trigger="app.isMobile ? 'click' : 'hover'"
        :content-style="tooltipContentStyle"
      >
        <template #trigger>
          <nd-button type="icon" size="mini" icon="alert-current-color" icon-color="gray-80" :class="isSourceTooltipVisible ? 'bg-gray-40' : ''" />
        </template>
        <p class="max-w-72 whitespace-pre-line text-sm text-black-50">
          {{ $t('review_platform_hint', { platform: externalSource }) }}
        </p>
      </n-tooltip>
      <span class="text-sm text-gray-80">{{ $t('review_platform', { platform: externalSource }) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { parseISO } from 'date-fns'
import { NRate, NTooltip } from 'naive-ui'
import type { FreviewReviewData } from '@/types/api'
import { useAppStore } from '@/store/app'
import { ReviewSource } from '@/types/review'

const SOURCE_PLATFORM_MAP: Partial<Record<ReviewSource, string>> = {
  [ReviewSource.FUNNOW]: 'FunNow',
  [ReviewSource.EATIGO]: 'Eatigo',
}

const tooltipContentStyle = {
  padding: '20px 16px',
  borderRadius: '4px',
  boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.2)',
  backgroundColor: '#fff',
}

const props = defineProps<{
  comment: FreviewReviewData
}>()

const app = useAppStore()
const { format } = await useI18nDateFns()

const { comment } = toRefs(props)
const { commenterAvatarImagePath, avatarBackgroundColorClass } = useMembershipLevel(comment)

const isSourceTooltipVisible = ref(false)

const commenterName = computed(() => props.comment.rated_by)
const { shouldShowDefaultAvatar, commenterFirstLetter, displayCommenterName } = useComment(commenterName)
const commentDate = computed(() => (props.comment.rate_time ? format(parseISO(props.comment.rate_time), 'yyyy/MM/dd') : ''))

const externalSource = computed(() => {
  return SOURCE_PLATFORM_MAP[props.comment.source as ReviewSource] ?? null
})
</script>

<style lang="scss" scoped>
:deep(.n-rate__item + .n-rate__item) {
  margin-left: 0;
}
</style>
